/**
 * Expansion speaker model selector — fully interactive dropdown that lists the
 * speaker models available for the current expansion type (ceiling or pendant).
 *
 * Mirrors the structure of `SpeakerModelSelector` but reads from / writes to the
 * expansion-speaker slot (`area.expansionSpeakers`) rather than the primary
 * `area.speakers`. Filters the equipment catalog by mounting type the same way:
 * `ceiling` → entries with `isCeilingSpkr` or key starting with `ceilingspkr`;
 * `pendant` → entries with `isPendantSpkr` or key starting with `pendantspkr`.
 * Selecting a different model dispatches `setExpansionSpeakerModel` which
 * rewrites `area.expansionSpeakers.{model, modelName}` and resets `qty`/`layout`
 * so the next recalc repopulates placement for the new model.
 */
import React, { useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Select from '../atoms/Select';
import InputLabel from '../atoms/InputLabel';
import { getAreaProperty, getCurrentValue } from 'reduxModules/selectors/containerSelectors';
import ReducersIndex from '../../reduxModules/ducks';
import toolIndex from '../../toolConstants';

interface SpeakerModelEntry {
  key: string;
  modelName: string;
}

interface Props {
  show?: boolean;
  disabled?: boolean;
  label?: string;
}

const ExpansionSpeakerModelSelector: React.FC<Props> = ({ show = true, disabled = false, label }) => {
  const dispatch = useDispatch();

  const expansionSpeakerType = useSelector(state =>
    getAreaProperty(state, 'expansionSpeakerType')
  ) as 'ceiling' | 'pendant' | 'none' | null;
  const currentModel = useSelector(state =>
    getAreaProperty(state, 'expansionSpeakers.model')
  ) as string | undefined;

  const currentTool = useSelector(state => getCurrentValue(state, 'currentTool')) || 'conferenceroomDesigner';
  const speakerConstants = (toolIndex as any)[currentTool]?.constants?.equipment?.speakers;

  // Filter speaker equipment by the active expansion type. Reuse the same generic-key
  // skiplist that `SpeakerModelSelector` uses — those entries are duplicates of the
  // canonical keys and shouldn't appear twice in the dropdown.
  const availableModels = useMemo<SpeakerModelEntry[]>(() => {
    if (!speakerConstants || (expansionSpeakerType !== 'ceiling' && expansionSpeakerType !== 'pendant')) return [];

    const genericKeys = new Set([
      'ceilingspkr_lp',
      'ceilingspkr_lp_taa',
      'ceilingspkr-mask6c',
    ]);

    const models: SpeakerModelEntry[] = [];
    Object.entries(speakerConstants).forEach(([key, value]: [string, any]) => {
      if (genericKeys.has(key)) return;
      const matchCeiling = expansionSpeakerType === 'ceiling' && (value.isCeilingSpkr || key.startsWith('ceilingspkr'));
      const matchPendant = expansionSpeakerType === 'pendant' && (value.isPendantSpkr || key.startsWith('pendantspkr'));
      if (matchCeiling || matchPendant) {
        models.push({ key, modelName: value.modelName || key });
      }
    });

    models.sort((a, b) => a.modelName.localeCompare(b.modelName));
    return models;
  }, [speakerConstants, expansionSpeakerType]);

  // If the active model isn't valid for the current type (e.g. user just switched
  // type from ceiling→pendant), self-correct to the first available so the dropdown
  // always reflects a real selection.
  const isCurrentModelValid = currentModel && availableModels.some(m => m.key === currentModel);
  const selectedValue = isCurrentModelValid ? (currentModel as string) : (availableModels[0]?.key || '');

  useEffect(() => {
    if (show && availableModels.length > 0 && !isCurrentModelValid && selectedValue) {
      dispatch((ReducersIndex.container as any).setExpansionSpeakerModel(selectedValue));
    }
  }, [availableModels, isCurrentModelValid, selectedValue, dispatch, show]);

  const handleChange = (event: any) => {
    const next = event.target.value;
    dispatch((ReducersIndex.container as any).setExpansionSpeakerModel(next));
  };

  const modelBubbles = availableModels.reduce<Record<string, string>>((acc, model) => {
    acc[model.key] = model.modelName;
    return acc;
  }, {});

  if (!show || availableModels.length === 0) return null;

  return (
    <Box className="d-flex flex-column" sx={{ width: '100%' }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={selectedValue}
        items={availableModels.map(m => m.key)}
        onChange={handleChange}
        disabled={disabled}
        placeHolder="Select expansion speaker model"
        bubbles={modelBubbles}
      />
    </Box>
  );
};

export default ExpansionSpeakerModelSelector;
